import { inventoryCatalog, inventoryDefinition } from '../../inventory/definition.js';
// Solid prop cover: selected mesh geometry, or the size tier when bounds are unavailable.
import { floorAt, type ShapeCoverVolume } from '../../../../../ft3d/engine/ft3d-shape-core.js';
import { dedicatedPropSpace, inventoryMeshDrawn, selectedPropMeshSpaceOrTier } from '../../inventory/prop-mesh-space.js';
import { inventorySolid, normalizeTransform, physicalMeshBoundsFt, type FootprintInputs } from '../../inventory/floor-move.js';
import { inventorySceneryShell, inventoryAuthoredSizeId, inventoryEncounterLink, inventoryEquipmentSlot, inventoryFixtureFlags, inventoryPropName } from '../../inventory/solid-obstacle.js';
import { boundCurtainIds, SIZE_WIDTHS_FT } from '../../inventory/lift-occupancy.js';
import { type JanusCtx, type Row, definitionNotHeld, handBack, record, upper } from '../context.js';

const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
const inv = (row: Row): Row => record(row.inventory) ?? row;
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const uuidOf = (value: unknown): string | null => {
  const raw = scalar(value);
  const id = upper(typeof raw === 'string' ? raw : record(record(raw)?.items)?.item_id ?? record(raw)?.item_id);
  return UUID.test(id) ? id : null;
};
/** PHP empty(). */
const phpEmpty = (value: unknown): boolean => {
  const v = scalar(value);
  return v === undefined || v === null || v === '' || v === '0' || v === 0 || v === false || (Array.isArray(value) && value.length === 0);
};
/** get_size_dimensions($id)['width'] — the page's SIZE_TYPES catalog, else the shared size ladder. */
function sizeWidth(id: string): number | null {
  const list = (globalThis as unknown as { SIZE_TYPES?: unknown[] }).SIZE_TYPES;
  const entry = Array.isArray(list) ? list.map(record).find(t => upper(t?.item_id) === upper(id)) : null;
  const n = Number(entry?.base_size);
  return Number.isFinite(n) && n > 0 ? n : SIZE_WIDTHS_FT[upper(id)] ?? null;
}
function position(row: Row): { x: number; y: number; z: number } {
  const at = record(row.position) ?? inv(row);
  const n = (v: unknown): number => { const x = Number(v); return Number.isFinite(x) ? x : 0; };
  return { x: n(at.x_pos), y: n(at.y_pos), z: n(at.z_pos) };
}

/** Solid prop occluders, or the exact physical volume of a piloted source (even when it grants no cover). */
export function propCoverVolumes(ctx: JanusCtx, shape: Row, rawShape: unknown, excludeIds: readonly string[], why: string, sourceId?: string): ShapeCoverVolume[] {
  const inventories = ctx.encounter.encounter_inventories_arr;
  if (!Array.isArray(inventories)) handBack(`${why}:inventory-roster-not-held`);
  const exclude = new Set(excludeIds.map(upper).filter(Boolean));
  const curtains = boundCurtainIds(rawShape);
  if (!curtains) handBack(`${why}:curtain-bindings-not-held`);
  const catalog = record(ctx.encounter.catalog) ?? {};
  const volumes: ShapeCoverVolume[] = [];
  for (const entry of inventories) {
    const row = record(entry);
    if (!row) handBack(`${why}:inventory-roster-not-held`);
    const i = inv(row);
    const id = upper(i.inventory_id);
    if (!id) handBack(`${why}:inventory-roster-not-held`);
    if (Number(i.inventory_deleted ?? 0) === 1) continue;
    if (sourceId && id !== upper(sourceId)) continue;
    const definition = inventoryDefinition(row, catalog, ctx.read.definition);
    if (!definition) definitionNotHeld(`${why}:prop-template-not-held`, ctx.read.inventoryField?.(row, 'inventory_item_id') ?? i.inventory_item_id);
    const items = { ...(record(definition.items) ?? {}), ...(record(row.items) ?? {}) };
    const itemType = String(items.item_type ?? '');
    let generation: unknown = i.inventory_generation;
    if (typeof generation === 'string') { try { generation = JSON.parse(generation); } catch { generation = null; } }
    // ft_inventory_is_solid_obstacle() — the floor-move lane's reviewed port.
    // Full instances may override the catalog; lean rows inherit through the page's layer reader.
    const instanceValue = (key: string): unknown => { const v = ctx.read.value(row, key); return v === undefined || v === null ? ctx.read.value(definition, key) : v; };
    const sizeId = inventoryAuthoredSizeId(row, definition);
    const solid = inventorySolid({ sceneryShell: inventorySceneryShell(row), private: Number(i.inventory_private ?? 0) !== 0, itemType, name: inventoryPropName(row, definition),
      ...inventoryFixtureFlags(instanceValue),
      meshDrawn: inventoryMeshDrawn(row, definition, inventoryCatalog(row, catalog, definition)),
      equipmentSlot: inventoryEquipmentSlot(row, definition), quantity: Number(i.item_qty ?? i.inventory_quantity ?? 0) || 0,
      encounterLink: inventoryEncounterLink(row), wallRun: !!record(record(generation)?.wallRun), corpseLinked: uuidOf(i.inventory_encounter_creature_id) !== null });
    if (!sourceId && (!solid || curtains.has(id) || exclude.has(id)
      || !phpEmpty(instanceValue('object_does_not_grant_environmental_cover')))) continue;
    const transform = normalizeTransform(i.transform_3d);
    const at = position(row);
    // Persisted size override, else catalog size; compiled instance defaults are not authored tiers.
    const size = sizeId ? sizeWidth(sizeId) ?? 5 : 5;
    let meshSpace: ReturnType<typeof selectedPropMeshSpaceOrTier> = null;
    if (!dedicatedPropSpace(itemType)) {
      try { meshSpace = selectedPropMeshSpaceOrTier(row, definition, inventoryCatalog(row, catalog, definition)); }
      catch (error) {
        const reason = error instanceof Error ? error.message : 'prop-mesh-not-held';
        handBack(`${why}:${reason}`);
      }
    }
    const input: FootprintInputs = { itemType, x: at.x, y: at.y, z: at.z, transform, sizeId, inventorySize: uuidOf(i.inventory_size),
      templateSizeId: uuidOf(ctx.read.value(definition, 'size')), worldMeshBoundsM: meshSpace?.boundsM ?? null,
      worldMeshAuthoredSizeTypeId: meshSpace?.authoredSizeTypeId, shape };
    const mesh = physicalMeshBoundsFt(input, (sid) => sizeWidth(sid));
    const base = Math.max(floorAt(shape as never, at.x, at.y) ?? 0, at.z);
    const zMax = base + (mesh ? mesh[1] : size);
    const facing = -((transform?.rotationDegrees ?? 0) * Math.PI / 180);
    const volume: ShapeCoverVolume = mesh
      ? { kind: 'oblong', x: at.x, y: at.y, halfW: mesh[0] / 2, halfD: mesh[2] / 2, facing, zMin: base, zMax } as ShapeCoverVolume
      : { kind: 'circle', x: at.x, y: at.y, r: size / 2, zMin: base, zMax };
    if (![at.x, at.y, base, zMax].every(Number.isFinite)) handBack(`${why}:prop-volume-invalid`);
    volumes.push(volume);
  }
  return volumes;
}
